// TechSystemsPanel.jsx - Purchase and manage airline technology systems

import React from 'react';
import { TECH_SYSTEMS, canPurchaseTech } from '../../systems/TechSystems';

export function TechSystemsPanel({ techState, cash, tier, onPurchase, onClose }) {
  const owned = techState?.owned || {};

  // Sort: owned first, then by tier requirement
  const systems = Object.values(TECH_SYSTEMS).sort((a, b) => {
    const aOwned = !!owned[a.id];
    const bOwned = !!owned[b.id];
    if (aOwned && !bOwned) return -1;
    if (!aOwned && bOwned) return 1;
    return a.tierRequired - b.tierRequired;
  });

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        
        <div style={styles.header}>
          <div style={styles.headerTitle}>TECHNOLOGY SYSTEMS</div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        <div style={styles.intro}>
          Enterprise software that transforms your operations. Each system requires significant investment but provides ongoing competitive advantages.
        </div>

        {/* Monthly Summary */}
        {techState?.totalMonthlyMaintenance > 0 && (
          <div style={styles.summary}>
            Monthly maintenance: ${formatMoney(techState.totalMonthlyMaintenance)}
          </div>
        )}

        <div style={styles.grid}>
          {systems.map(tech => {
            const isOwned = !!owned[tech.id];
            const purchaseCheck = canPurchaseTech(techState, tech.id, cash, tier);
            
            return (
              <TechCard
                key={tech.id}
                tech={tech}
                isOwned={isOwned}
                canPurchase={purchaseCheck.canPurchase}
                lockReason={purchaseCheck.reason}
                currentTier={tier}
                onPurchase={() => onPurchase(tech.id)}
              />
            );
          })}
        </div>

      </div>
    </div>
  );
}

function TechCard({ tech, isOwned, canPurchase, lockReason, currentTier, onPurchase }) {
  const tierLocked = currentTier < tech.tierRequired;
  
  return (
    <div style={{
      ...styles.card,
      ...(isOwned ? styles.cardOwned : {}),
      ...(tierLocked ? styles.cardTierLocked : {}),
    }}>
      {/* Header */}
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.techName}>{tech.name}</div>
          <div style={styles.techTagline}>{tech.tagline}</div>
        </div>
        {isOwned && <div style={styles.ownedBadge}>ACTIVE</div>}
        {tierLocked && <div style={styles.tierBadge}>TIER {tech.tierRequired}</div>}
      </div>

      {/* Description */}
      <div style={styles.description}>{tech.description}</div>

      {/* Effects */}
      <div style={styles.effectsSection}>
        <div style={styles.effectsTitle}>CAPABILITIES</div>
        <div style={styles.effectsList}>
          {formatTechEffects(tech.gameplay)}
        </div>
      </div>

      {/* Cost */}
      <div style={styles.cardFooter}>
        {isOwned ? (
          <div style={styles.maintenanceCost}>
            ${formatMoney(tech.monthlyMaintenance)}/mo maintenance
          </div>
        ) : (
          <>
            <div>
              <div style={styles.purchasePrice}>${formatMoney(tech.purchasePrice)}</div>
              <div style={styles.maintenanceNote}>+ ${formatMoney(tech.monthlyMaintenance)}/mo</div>
            </div>
            <button
              style={{
                ...styles.purchaseBtn,
                ...(canPurchase ? {} : styles.btnLocked),
              }}
              onClick={onPurchase}
              disabled={!canPurchase}
            >
              {tierLocked ? `Tier ${tech.tierRequired} Required` : 
               !canPurchase ? lockReason : 'Purchase'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function formatTechEffects(gameplay) {
  const items = [];
  
  if (gameplay.preventsCrewCancellations) items.push('Prevents crew-related cancellations');
  if (gameplay.showsCrewStatusDashboard) items.push('Real-time crew status dashboard');
  if (gameplay.alertsBeforeViolation) items.push('Early warning alerts');
  if (gameplay.reducesCrewRelatedDelays) items.push(`${Math.round(gameplay.reducesCrewRelatedDelays * 100)}% fewer crew delays`);
  
  if (gameplay.reducesDeadheadCosts) items.push('Eliminates deadhead costs');
  if (gameplay.autoPositionsCrew) items.push('Automatic crew positioning');
  if (gameplay.reducesCrewShortages) items.push(`${Math.round(gameplay.reducesCrewShortages * 100)}% fewer shortages`);
  
  if (gameplay.increasesRevenue) items.push(`+${Math.round(gameplay.increasesRevenue * 100)}% revenue optimization`);
  if (gameplay.showsDemandForecast) items.push('Demand forecasting');
  if (gameplay.reducesEmptySeats) items.push(`${Math.round(gameplay.reducesEmptySeats * 100)}% better load factors`);
  
  if (gameplay.reducesGroundings) items.push(`${Math.round(gameplay.reducesGroundings * 100)}% fewer AOG events`);
  if (gameplay.reducesMaintCosts) items.push(`${Math.round(gameplay.reducesMaintCosts * 100)}% lower maintenance costs`);
  if (gameplay.showsAircraftHealth) items.push('Aircraft health monitoring');
  
  return items.map((item, i) => (
    <div key={i} style={styles.effectItem}>{item}</div>
  ));
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(0)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '900px', maxHeight: '90vh', overflow: 'auto',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  intro: {
    padding: '16px 20px', fontSize: '11px', color: '#666', lineHeight: '1.5',
    borderBottom: '1px solid #1a1a1a',
  },
  summary: {
    padding: '12px 20px', backgroundColor: '#111', fontSize: '11px', color: '#888',
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px',
    padding: '20px',
  },
  
  card: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '8px',
    padding: '18px', transition: 'border-color 0.15s',
  },
  cardOwned: {
    borderColor: '#00ff88', backgroundColor: '#0a1a0a',
  },
  cardTierLocked: {
    opacity: 0.6,
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '12px',
  },
  techName: {
    fontSize: '16px', fontWeight: '700', color: '#fff', letterSpacing: '1px',
  },
  techTagline: {
    fontSize: '10px', color: '#00ff88', marginTop: '4px', fontStyle: 'italic',
  },
  ownedBadge: {
    fontSize: '9px', fontWeight: '600', letterSpacing: '1px',
    padding: '3px 8px', backgroundColor: '#00ff88', color: '#000', borderRadius: '3px',
  },
  tierBadge: {
    fontSize: '9px', fontWeight: '600', letterSpacing: '1px',
    padding: '3px 8px', border: '1px solid #666', color: '#666', borderRadius: '3px',
  },
  description: {
    fontSize: '11px', color: '#888', lineHeight: '1.5', marginBottom: '16px',
  },
  effectsSection: {
    marginBottom: '16px',
  },
  effectsTitle: {
    fontSize: '9px', letterSpacing: '1px', color: '#666', marginBottom: '8px',
  },
  effectsList: {},
  effectItem: {
    fontSize: '10px', color: '#00ff88', padding: '3px 0',
    paddingLeft: '12px', position: 'relative',
  },
  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    borderTop: '1px solid #222', paddingTop: '14px', marginTop: '14px',
  },
  purchasePrice: {
    fontSize: '18px', fontWeight: '700', color: '#fff',
  },
  maintenanceNote: {
    fontSize: '10px', color: '#666',
  },
  maintenanceCost: {
    fontSize: '11px', color: '#888',
  },
  purchaseBtn: {
    background: 'none', border: '1px solid #00ff88', borderRadius: '4px',
    padding: '10px 20px', fontSize: '11px', fontWeight: '600',
    color: '#00ff88', cursor: 'pointer', fontFamily: 'inherit',
    letterSpacing: '1px',
  },
  btnLocked: {
    borderColor: '#333', color: '#666', cursor: 'not-allowed',
  },
};

export default TechSystemsPanel;
